import { useMemo, useState } from "react";
import {
  PageShell,
  PageHeader,
  ListSkeleton,
  EmptyState,
  ListToolbar,
} from "../../../Components/shared";
import {
  FaLayerGroup,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import useAuth from "../../../Hooks/useAuth";
import {
  useGetPendingDevicesQuery,
  useGetPendingBootstrapRequestsQuery,
} from "../../../store";
import { usePendingDevicesSocket } from "../../../Hooks/storageRoles/usePendingDevicesSocket";
import { usePendingBootstrapSocket } from "../../../Hooks/storageRoles/usePendingBootstrapSocket";
import PendingClientCard from "../../../Components/pendingDevices/storageRoles/PendingClientCard";
import PendingClientDrawer from "../../../Components/pendingDevices/storageRoles/PendingClientDrawer";
import NewInstallDrawer from "../../../Components/pendingDevices/storageRoles/NewInstallDrawer";
import ScreenReplaceDrawer from "../../../Components/pendingDevices/storageRoles/ScreenReplaceDrawer";
import { derivePhase } from "../../../Components/pendingDevices/storageRoles/PendingClientCard";

const FILTERS = [
  { id: "active", label: "Aktif", icon: FaClock },
  { id: "completed", label: "Tamamlanan", icon: FaCheckCircle },
  { id: "rejected", label: "Reddedilen", icon: FaTimesCircle },
  { id: "all", label: "Tümü", icon: FaLayerGroup },
];

const ACTIVE_PHASES = new Set([
  "bootstrap_pending",
  "awaiting_mode",
  "mode_pending",
  "mode_claimed",
]);
const REJECTED_PHASES = new Set(["bootstrap_rejected", "mode_rejected"]);
const COMPLETED_PHASES = new Set(["completed"]);

function clientActivityTs(client) {
  const candidates = [
    client.pending?.assignedAt,
    client.pending?.claimedAt,
    client.pending?.lastSeen,
    client.pending?.firstSeen,
    client.pending?.createdAt,
    client.bootstrap?.credentialsIssuedAt,
    client.bootstrap?.approvedAt,
    client.bootstrap?.updatedAt,
    client.bootstrap?.createdAt,
  ];
  for (const c of candidates) {
    if (c) return new Date(c).getTime();
  }
  return 0;
}

/**
 * Onay Bekleyen Cihazlar — birlestirilmis tek-kart goruntu.
 * Her tempClientId icin BootstrapRequest + PendingDevice tek karta sigar.
 * Tum aksiyonlar PendingClientDrawer'da; form gerektiren modlar
 * (yeni kurulum / ekran degisimi) icin standalone drawer'lar acilir.
 */
export default function PendingDevicesPage() {
  const { auth } = useAuth();
  const token = auth?.accessToken;

  const [filter, setFilter] = useState("active");
  const [activeClient, setActiveClient] = useState(null);
  const [activeForm, setActiveForm] = useState(null); // pending doc for sub-drawer

  // Tum bootstrap kayitlari (status filter yok — front-end filter)
  const {
    data: bootstrapItems = [],
    isLoading: isBootstrapLoading,
    refetch: refetchBootstrap,
  } = useGetPendingBootstrapRequestsQuery(
    { token },
    { pollingInterval: 60_000 }
  );

  // Tum pending kayitlari (status filter yok)
  const {
    data: pendingItems = [],
    isLoading: isPendingLoading,
    refetch: refetchPending,
  } = useGetPendingDevicesQuery({ token }, { pollingInterval: 60_000 });

  const {
    socketItems: pendingSocketItems,
    removedIds: pendingRemovedIds,
    newItemIds: pendingNewIds,
  } = usePendingDevicesSocket(token);
  const {
    socketItems: bootstrapSocketItems,
    newItemIds: bootstrapNewIds,
    removedIds: bootstrapRemovedIds,
  } = usePendingBootstrapSocket(token);

  // API + socket merge — bootstrap
  const mergedBootstrap = useMemo(() => {
    const map = new Map();
    for (const it of bootstrapItems) map.set(it._id, it);
    for (const it of bootstrapSocketItems) map.set(it._id, it);
    for (const id of bootstrapRemovedIds) map.delete(id);
    return Array.from(map.values());
  }, [bootstrapItems, bootstrapSocketItems, bootstrapRemovedIds]);

  // API + socket merge — pending
  const mergedPending = useMemo(() => {
    const map = new Map();
    for (const it of pendingItems) map.set(it._id, it);
    for (const it of pendingSocketItems) map.set(it._id, it);
    for (const id of pendingRemovedIds) map.delete(id);
    return Array.from(map.values());
  }, [pendingItems, pendingSocketItems, pendingRemovedIds]);

  // Birlestirme — tempClientId bazli tek client objesi
  const clients = useMemo(() => {
    const byTcid = new Map();
    for (const b of mergedBootstrap) {
      if (!b.tempClientId) continue;
      byTcid.set(b.tempClientId, {
        tempClientId: b.tempClientId,
        bootstrap: b,
        pending: null,
      });
    }
    for (const p of mergedPending) {
      if (!p.tempClientId) continue;
      const existing = byTcid.get(p.tempClientId);
      if (existing) {
        existing.pending = p;
      } else {
        byTcid.set(p.tempClientId, {
          tempClientId: p.tempClientId,
          bootstrap: null,
          pending: p,
        });
      }
    }
    return Array.from(byTcid.values()).sort(
      (a, b) => clientActivityTs(b) - clientActivityTs(a)
    );
  }, [mergedBootstrap, mergedPending]);

  // Filter
  const filteredClients = useMemo(() => {
    if (filter === "all") return clients;
    if (filter === "active")
      return clients.filter((c) =>
        ACTIVE_PHASES.has(derivePhase(c))
      );
    if (filter === "rejected")
      return clients.filter((c) =>
        REJECTED_PHASES.has(derivePhase(c))
      );
    if (filter === "completed")
      return clients.filter((c) =>
        COMPLETED_PHASES.has(derivePhase(c))
      );
    return clients;
  }, [clients, filter]);

  // Tab badge sayilari
  const counts = useMemo(() => {
    const c = { active: 0, completed: 0, rejected: 0, all: clients.length };
    for (const cl of clients) {
      const ph = derivePhase(cl);
      if (ACTIVE_PHASES.has(ph)) c.active += 1;
      else if (COMPLETED_PHASES.has(ph)) c.completed += 1;
      else if (REJECTED_PHASES.has(ph)) c.rejected += 1;
    }
    return c;
  }, [clients]);

  const isLoading = isBootstrapLoading || isPendingLoading;
  const refetchAll = () => {
    refetchBootstrap();
    refetchPending();
  };

  const handleOpenClient = (client) => setActiveClient(client);
  const handleCloseDrawer = () => setActiveClient(null);

  // Drawer "Forma Git" callback'i — pending doc'unu mode'a gore ilgili
  // standalone drawer'a teslim eder, unified drawer kapanir.
  const handleOpenForm = (pending) => {
    setActiveClient(null);
    setActiveForm(pending);
  };
  const handleCloseForm = () => setActiveForm(null);

  // active client refresh on socket update — listede mevcut client'i bul
  const liveActiveClient = useMemo(() => {
    if (!activeClient) return null;
    return (
      clients.find((c) => c.tempClientId === activeClient.tempClientId) ||
      activeClient
    );
  }, [activeClient, clients]);

  const isNewClient = (client) => {
    if (client.bootstrap?._id && bootstrapNewIds.has(client.bootstrap._id))
      return true;
    if (client.pending?._id && pendingNewIds.has(client.pending._id))
      return true;
    return false;
  };

  return (
    <PageShell>
      <div className="px-3 py-2.5 sm:p-4 md:p-6 bg-gray-50/80 sm:bg-gray-50 shrink-0">
        <PageHeader
          icon={FaClock}
          iconColor="amber"
          title="Onay Bekleyen Cihazlar"
          subtitle="Bağlantı + kurulum onaylarını tek karttan adım adım yönetin."
          stats={[
            { label: "İstek", value: clients.length, icon: FaLayerGroup, color: "amber" },
          ]}
          isLoading={isLoading}
          mobileSlotId="pending-devices-toolbar-mobile-slot"
        />
      </div>

      <ListToolbar
        className="px-3 pb-2.5 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
        mobileFilterSlotId="pending-devices-toolbar-mobile-slot"
        filterTabs={{
          filters: FILTERS.map((f) => ({ ...f, count: counts[f.id] || 0 })),
          activeFilter: filter,
          onChange: setFilter,
          totalCount: counts[filter] || 0,
          isLoading,
          color: "amber",
        }}
        onClear={() => setFilter("active")}
      />

      <div className="flex-1 min-h-0 overflow-auto">
        <div className="relative w-full p-3 sm:p-4 md:p-6">
          {isLoading ? (
            <ListSkeleton
              variant="cards"
              message="Bekleyen cihazlar yükleniyor..."
            />
          ) : filteredClients.length === 0 ? (
            <EmptyState
              message={
                filter === "active"
                  ? "Aktif istek yok"
                  : filter === "completed"
                    ? "Tamamlanan istek yok"
                    : filter === "rejected"
                      ? "Reddedilmiş istek yok"
                      : "Hiç istek yok"
              }
              suggestion="Cihazlar onay isteği gönderdiğinde burada görünür."
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
              {filteredClients.map((client) => (
                <PendingClientCard
                  key={client.tempClientId}
                  client={client}
                  onOpen={handleOpenClient}
                  isNew={isNewClient(client)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Birlestirilmis drawer — tum aksiyonlar burada */}
      <PendingClientDrawer
        open={!!liveActiveClient}
        client={liveActiveClient}
        onClose={handleCloseDrawer}
        onSuccess={refetchAll}
        onOpenForm={handleOpenForm}
      />

      {/* Form gerektiren modlar icin standalone drawer'lar */}
      <NewInstallDrawer
        open={activeForm?.mode === "new_install"}
        pending={activeForm}
        onClose={handleCloseForm}
        onSuccess={() => {
          refetchAll();
          handleCloseForm();
        }}
      />
      <ScreenReplaceDrawer
        open={activeForm?.mode === "screen_replacement"}
        pending={activeForm}
        onClose={handleCloseForm}
        onSuccess={() => {
          refetchAll();
          handleCloseForm();
        }}
      />
    </PageShell>
  );
}
